import {push} from "connected-react-router";
import {change, reset, setSubmitSucceeded, startSubmit, stopSubmit,} from "redux-form";
import {call, fork, put, select, take} from "redux-saga/effects";
import {GetMeResponse} from "src/presentation/response/auth_response";
import {NotificationClient} from "../../config/view_const/notification";
import {AppError, ErrorType} from "../../domain/model/app_error";
import {AppNotificationType} from "../../domain/model/app_notification_message";
import {Client} from "../../domain/model/client";
import {Company} from "../../domain/model/company";
import {User} from "../../domain/model/user";
import {IClientRepository} from "../../domain/repository/client_repository";
import {ICompanyRepository, TUpdateCompanyParams,} from "../../domain/repository/company_repository";
import {ICustomerRepository} from "../../domain/repository/customer_repository";
import {IUserRepository} from "../../domain/repository/user_repository";
import {createAuthUseCase, IAuthUseCase,} from "../../domain/usecase/auth_usecase";
import {createFileUsecase, IFileUseCase,} from "../../domain/usecase/file_usecase";
import {createApiClient, IApiClient} from "../../infra/api/client";
import {createClientAPI} from "../../infra/api/service/client_api";
import {createCompanyAPI} from "../../infra/api/service/company_api";
import {createCustomerAPI} from "../../infra/api/service/customer_api";
import {createUserAPI} from "../../infra/api/service/user_api";
import {
  createAppNotificationMessageActionCreator,
  IAppNotificationMessageActionCreator,
} from "../action/app_notification_message_action";
import {createAuthActionCreator, IAuthActionCreator,} from "../action/auth_action";
import {
  ClientMypageActionType,
  createClientMypageActionCreator,
  IClientMypageActionCreator,
  IRequestUpdateClientAction,
  IRequestUpdateCompanyAction,
  IRequestUpdateEmailAction,
  IRequestUpdatePasswordAction,
} from "../action/client_mypage_action";
import {AddAppNotificationRequest} from "../request/app_notification_message_request";
import {
  GetMeClientByClientResponse,
  UpdateClientByClientResponse,
  UpdateCompanyByClientResponse,
} from "../response/mypage_response";
import {AppState} from "../store/app_state";
import {AuthState} from "../store/auth_state";
import {handleErrorForHandler} from "./handleErrorForHandler";

const apiClient: IApiClient = createApiClient();
const userRepository: IUserRepository = createUserAPI(apiClient);
const companyRepository: ICompanyRepository = createCompanyAPI(apiClient);
const clientRepository: IClientRepository = createClientAPI(apiClient);
const customerRepository: ICustomerRepository = createCustomerAPI(apiClient);
const authUseCase: IAuthUseCase = createAuthUseCase(
    userRepository,
    customerRepository
);
const fileUseCase: IFileUseCase = createFileUsecase();
const actionCreator: IClientMypageActionCreator =
    createClientMypageActionCreator();
const actionCreatorForAuth: IAuthActionCreator = createAuthActionCreator();
const actionCreatorForNotification: IAppNotificationMessageActionCreator =
    createAppNotificationMessageActionCreator();

function* handleGetMeClientInClientMypage() {
    while (true) {
        try {
            yield take(ClientMypageActionType.REQUEST_GET_ME_CLIENT);
            const user: User = yield call(getMeClient);
            if (user.client) {
                const client: Client = user.client;
                const email: string = user.email;
                client.email = email;
                const company: Company = yield call(getCompany, client.companyID);
                const res: GetMeClientByClientResponse =
                    new GetMeClientByClientResponse();
                res.client = client;
                res.company = company;
                yield put(actionCreator.callbackGetMeClientAction(true, res));
            } else {
                throw new AppError(
                    "UNKNOWN CODE",
                    ErrorType.CLIENT_ERROR,
                    "異常が発生しました"
                );
            }
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(actionCreator.callbackGetMeClientAction(false));
        }
    }
}

function* handleUpdateCompanyInClientMypage() {
    while (true) {
        const formName: string = "ClientMypageMainCompanyForm";
        try {
            const action: IRequestUpdateCompanyAction = yield take(
                ClientMypageActionType.REQUEST_UPDATE_COMPANY
            );
            yield put(startSubmit(formName));
            const authSelector = (state: AppState) => state.authReducer;
            const _authState: AuthState = yield select(authSelector);
            const user: User | null = _authState.user;

            let logoURL: string;
            if (action.item.uploadFileLogoURL) {
                logoURL = yield call(
                    createCompanyLogo,
                    user,
                    action.item.uploadFileLogoURL
                );
            } else if (/.*\/company\/default.*/.test(action.item.logoURL)) {
                // company/default が含まれているならdefault 画像
                logoURL = "";
            } else {
                logoURL = action.item.logoURL;
            }

            const updateParams: TUpdateCompanyParams = {
                name: action.item.name,
                logoURL,
                webURL: action.item.webURL,
                establishedAt: action.item.establishedAt,
                postalCode: action.item.postalCode,
                place: action.item.place,
                businesses: action.item.business,
                representativeName: action.item.representativeName,
                capitalStock: action.item.capitalStock,
                employeeScale: action.item.employeeScale,
                introduction: action.item.introduction,
                accordingCompanyName: action.item.accordingCompanyName,
                accordingCompanyPostalCode: action.item.accordingCompanyPostalCode,
                accordingCompanyAddress: action.item.accordingCompanyAddress,
                invoiceDepartmentName: action.item.invoiceDepartmentName,
            };

            const company: Company = yield call(updateCompany, updateParams);
            yield put(actionCreatorForAuth.requestGetMeAction());

            const res: UpdateCompanyByClientResponse =
                new UpdateCompanyByClientResponse();
            res.company = company;
            yield put(actionCreator.callbackUpdateCompanyAction(true, res));

            const me: User = yield call(getMeClient);
            const meRes: GetMeResponse = new GetMeResponse();
            meRes.user = me;
            meRes.company = company;
            yield put(actionCreatorForAuth.callbackGetMeAction(true, meRes));

            yield put(change(formName, "logoURL", logoURL));
            yield put(stopSubmit(formName));
            yield put(setSubmitSucceeded(formName));
            yield put(push(`/client/mypage/company`));

            const notificationReq: AddAppNotificationRequest =
                new AddAppNotificationRequest();
            notificationReq.type = AppNotificationType.INFO;
            notificationReq.message = NotificationClient.successUpdateCompany;
            yield put(actionCreatorForNotification.requestAddAction(notificationReq));
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(stopSubmit(formName, {_error: error.toString()}));
            yield put(actionCreator.callbackUpdateCompanyAction(false));
        }
    }
}

function* handleUpdateClientInClientMypage() {
    while (true) {
        const formName: string = "ClientMypageMainClientForm";
        try {
            const action: IRequestUpdateClientAction = yield take(
                ClientMypageActionType.REQUEST_UPDATE_CLIENT
            );
            yield put(startSubmit(formName));

            let iconURL: string;
            const authSelector = (state: AppState) => state.authReducer;
            const _authState: AuthState = yield select(authSelector);
            const user: User | null = _authState.user;
            if (action.item.uploadFileIconURL) {
                iconURL = yield call(
                    createClientIcon,
                    user,
                    action.item.uploadFileIconURL
                );
            } else if (/.*\/client\/default.*/.test(action.item.iconURL)) {
                // client/default が含まれているならdefault 画像
                iconURL = "";
            } else {
                iconURL = action.item.iconURL;
            }

            const client: Client = yield call(
                updateClient,
                action.item.name,
                action.item.nameKana,
                iconURL,
                action.item.phoneNumber,
                action.item.department,
                action.item.position
            );
            const res: UpdateClientByClientResponse =
                new UpdateClientByClientResponse();
            res.client = client;
            yield put(actionCreator.callbackUpdateClientAction(true, res));
            yield put(change(formName, "iconURL", iconURL));
            yield put(stopSubmit(formName));
            yield put(setSubmitSucceeded(formName));
            yield put(push(`/client/mypage/client`));

            const notificationReq: AddAppNotificationRequest =
                new AddAppNotificationRequest();
            notificationReq.type = AppNotificationType.INFO;
            notificationReq.message = NotificationClient.successUpdateClient;
            yield put(actionCreatorForNotification.requestAddAction(notificationReq));
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(stopSubmit(formName, {_error: error.toString()}));
            yield put(actionCreator.callbackUpdateClientAction(false));
        }
    }
}

function* handleUpdatePasswordInClientMypage() {
    while (true) {
        const formName: string = "ClientMypageMainPasswordForm";
        try {
            const action: IRequestUpdatePasswordAction = yield take(
                ClientMypageActionType.REQUEST_UPDATE_PASSWORD
            );
            yield put(startSubmit(formName));
            yield call(
                updatePassword,
                action.item.currentPassword,
                action.item.newPassword
            );
            yield put(actionCreator.callbackUpdatePasswordAction(true));
            yield put(stopSubmit(formName));
            yield put(setSubmitSucceeded(formName));
            yield put(reset(formName));

            const notificationReq: AddAppNotificationRequest =
                new AddAppNotificationRequest();
            notificationReq.type = AppNotificationType.INFO;
            notificationReq.message = NotificationClient.successUpdatePassword;
            yield put(actionCreatorForNotification.requestAddAction(notificationReq));
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(stopSubmit(formName, {_error: error.toString()}));
            yield put(actionCreator.callbackUpdatePasswordAction(false));
        }
    }
}

function* handleUpdateEmailInClientMypage() {
    while (true) {
        const formName: string = "ClientMypageMainEmailForm";
        try {
            const action: IRequestUpdateEmailAction = yield take(
                ClientMypageActionType.REQUEST_UPDATE_EMAIL
            );
            yield put(startSubmit(formName));
            yield call(updateEmail, action.item.currentPassword, action.item.email);
            yield put(actionCreator.callbackUpdateEmailAction(true));
            yield put(stopSubmit(formName));
            yield put(setSubmitSucceeded(formName));
            yield put(actionCreatorForAuth.requestGetMeAction());
            yield put(reset(formName));

            const notificationReq: AddAppNotificationRequest =
                new AddAppNotificationRequest();
            notificationReq.type = AppNotificationType.INFO;
            notificationReq.message = NotificationClient.successUpdateEmail;
            yield put(actionCreatorForNotification.requestAddAction(notificationReq));
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(stopSubmit(formName, {_error: error.toString()}));
            yield put(actionCreator.callbackUpdateEmailAction(false));
        }
    }
}

const getMeClient = (): Promise<User> => {
    return userRepository.getMe();
};

const getCompany = (companyId: string): Promise<Company> => {
    return companyRepository.get(companyId);
};

const updateCompany = (
    updateCompanyParams: TUpdateCompanyParams
): Promise<Company> => {
    return companyRepository.update(updateCompanyParams);
};

const updateClient = (
    name: string,
    nameKana: string,
    iconURL: string,
    phoneNumber: string,
    department: string,
    position: string
): Promise<Client> => {
    return clientRepository.update(
        name,
        nameKana,
        iconURL,
        phoneNumber,
        department,
        position
    );
};

const updatePassword = (
    currentPassword: string,
    password: string
): Promise<void> => {
    return authUseCase.updatePassword(currentPassword, password);
};

const updateEmail = (currentPassword: string, email: string) => {
    return authUseCase.updateEmail(currentPassword, email);
};

const createCompanyLogo = (user: User, uploadFile: File): Promise<string> => {
    return fileUseCase.createCompanyLogo(user, uploadFile);
};

const createClientIcon = (user: User, uploadFile: File): Promise<string> => {
    return fileUseCase.createClientIcon(user, uploadFile);
};

export {
    handleGetMeClientInClientMypage,
    handleUpdateClientInClientMypage,
    handleUpdateCompanyInClientMypage,
    handleUpdateEmailInClientMypage,
    handleUpdatePasswordInClientMypage,
};



// WEBPACK FOOTER //
// ./src/presentation/handler/client_mypage_handler.ts